import { DialogComponent } from '@theme/dialog';
import { fetchSection } from '@theme/utilities';

/**
 * Subscription Upsell Modal Component
 *
 * This component displays a modal after a product is added to cart,
 * offering a subscription upsell using Appstle Subscriptions.
 *
 * @extends DialogComponent
 */
export class SubscriptionUpsellModal extends DialogComponent {
  requiredRefs = ['dialog', 'productForm', 'acceptButton', 'declineButton'];

  constructor() {
    super();
    this.sessionStorageKey = 'subscription-upsell-shown';
    this.isProcessing = false;
    this._boundHandleCartAdded = this.handleCartAdded.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Listen for cart add events
    document.addEventListener('cart:added', this._boundHandleCartAdded);

    // Set up button listeners
    if (this.refs.acceptButton) {
      this.refs.acceptButton.addEventListener('click', this.handleAcceptUpsell.bind(this));
    }

    if (this.refs.declineButton) {
      this.refs.declineButton.addEventListener('click', this.handleDeclineUpsell.bind(this));
    }

    // Set up subscription option listeners
    this.setupSubscriptionOptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:added', this._boundHandleCartAdded);
  }

  /**
   * Sets up listeners for subscription option radio buttons
   */
  setupSubscriptionOptions() {
    if (this.refs.subscriptionOptions) {
      const radioButtons = this.refs.subscriptionOptions.querySelectorAll('input[type="radio"]');
      radioButtons.forEach(radio => {
        radio.addEventListener('change', this.handleSubscriptionChange.bind(this));
      });
    }
  }

  /**
   * Handles subscription option changes
   *
   * @param {Event} event - The change event
   */
  handleSubscriptionChange(event) {
    const selectedPlanId = event.target.value;
    const selectedVariantId = event.target.dataset.variantId;

    // Update the hidden selling plan input
    if (this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = selectedPlanId;
    }

    // Update the hidden variant ID input
    if (this.refs.variantIdInput) {
      this.refs.variantIdInput.value = selectedVariantId;
    }

    // Update the form data attributes
    if (this.refs.productForm) {
      this.refs.productForm.dataset.sellingPlan = selectedPlanId;
      this.refs.productForm.dataset.variantId = selectedVariantId;
    }

    // Update current config
    if (this.currentUpsellConfig) {
      this.currentUpsellConfig.sellingPlanId = selectedPlanId;
      this.currentUpsellConfig.variantId = selectedVariantId;
    }
  }

  /**
   * Handles the cart:added event and shows the upsell modal if applicable
   *
   * @param {CustomEvent} event - The cart:added event
   */
  async handleCartAdded(event) {
    // Check if we should show the modal
    if (!this.shouldShowModal(event)) {
      return;
    }

    // Get the upsell configuration
    const upsellConfig = await this.getUpsellConfiguration(event.detail);

    if (!upsellConfig || !upsellConfig.enabled) {
      return;
    }

    // Load the upsell product data
    await this.loadUpsellProduct(upsellConfig);

    // Show the modal
    this.showDialog();

    // Mark as shown in session if configured to show once
    const showOnce = this.dataset.showOnce !== 'false';
    if (showOnce) {
      this.markAsShown(event.detail.productId);
    }
  }

  /**
   * Determines if the modal should be shown
   *
   * @param {CustomEvent} event - The cart:added event
   * @returns {boolean} Whether to show the modal
   */
  shouldShowModal(event) {
    // Don't show if already processing
    if (this.isProcessing) {
      return false;
    }

    // Don't show if this was triggered by the upsell itself
    if (event.detail.isUpsell) {
      return false;
    }

    // Check display rules
    const triggerMode = this.dataset.triggerProducts || 'all';
    const productHandle = event.detail.productHandle;

    if (triggerMode === 'specific') {
      // Check if product is in the specific products list
      const specificProducts = (this.dataset.specificProducts || '').split(',').map(p => p.trim()).filter(p => p);

      if (specificProducts.length > 0 && !specificProducts.includes(productHandle)) {
        return false;
      }
    } else if (triggerMode === 'collections') {
      // For collections, we'd need additional data about product collections
      // This would require passing collection data from the product page
    }
    // If triggerMode is 'all', continue with showing the modal

    // Only check session storage if "show once" is enabled
    const showOnce = this.dataset.showOnce !== 'false';

    if (showOnce) {
      const shownProducts = this.getShownProducts();
      if (shownProducts.includes(event.detail.productId)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Gets the upsell configuration for a product
   *
   * @param {Object} productData - The product data from cart:added event
   * @returns {Object|null} The upsell configuration
   */
  async getUpsellConfiguration(productData) {
    // First check if product has metafield configuration
    const metafieldConfig = this.getMetafieldConfiguration();
    if (metafieldConfig) {
      return metafieldConfig;
    }

    // Otherwise check theme settings
    const themeConfig = this.getThemeConfiguration();
    if (themeConfig) {
      return themeConfig;
    }

    // Fallback to data attributes on the modal element
    return this.getDataAttributeConfiguration();
  }

  /**
   * Gets configuration from product metafields
   *
   * @returns {Object|null} Configuration object
   */
  getMetafieldConfiguration() {
    const metafieldData = this.dataset.productMetafields;
    if (!metafieldData) return null;

    try {
      const metafields = JSON.parse(metafieldData);
      if (!metafields.subscription_upsell) return null;

      return {
        enabled: true,
        productId: metafields.subscription_upsell.product_id,
        variantId: metafields.subscription_upsell.variant_id,
        sellingPlanId: metafields.subscription_upsell.selling_plan_id,
        title: metafields.subscription_upsell.title,
        description: metafields.subscription_upsell.description
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Gets configuration from theme settings
   *
   * @returns {Object|null} Configuration object
   */
  getThemeConfiguration() {
    // This would typically come from section settings
    const sectionSettings = window.theme?.settings?.subscriptionUpsell;
    if (!sectionSettings || !sectionSettings.enabled) return null;

    return {
      enabled: true,
      productId: sectionSettings.productId,
      variantId: sectionSettings.variantId,
      sellingPlanId: sectionSettings.sellingPlanId,
      title: sectionSettings.title,
      description: sectionSettings.description
    };
  }

  /**
   * Gets configuration from data attributes
   *
   * @returns {Object|null} Configuration object
   */
  getDataAttributeConfiguration() {
    // Check if modal is enabled in settings
    const isEnabled = this.dataset.enabled === 'true';

    if (!isEnabled) {
      return null;
    }

    if (!this.dataset.upsellProductId) {
      return null;
    }

    // Check if we have multiple plans or single plan
    const hasMultiplePlans = this.dataset.monthlyPlan && this.dataset.annualPlan;

    // Use monthly variant as default if multiple plans exist
    const defaultVariantId = hasMultiplePlans
      ? (this.dataset.monthlyVariant || this.dataset.upsellVariantId)
      : this.dataset.upsellVariantId;

    return {
      enabled: true,
      productId: this.dataset.upsellProductId,
      variantId: defaultVariantId,
      sellingPlanId: hasMultiplePlans ? this.dataset.monthlyPlan : this.dataset.upsellSellingPlanId,
      monthlyVariantId: this.dataset.monthlyVariant,
      monthlyPlanId: this.dataset.monthlyPlan,
      annualVariantId: this.dataset.annualVariant,
      annualPlanId: this.dataset.annualPlan,
      hasMultiplePlans: hasMultiplePlans,
      title: this.dataset.upsellTitle,
      description: this.dataset.upsellDescription
    };
  }

  /**
   * Loads the upsell product data and updates the modal content
   *
   * @param {Object} config - The upsell configuration
   */
  async loadUpsellProduct(config) {
    try {
      // Store config for later use
      this.currentUpsellConfig = config;

      // Update the modal content with product data
      if (this.refs.productTitle) {
        this.refs.productTitle.textContent = config.title || 'Subscribe & Save';
      }

      if (this.refs.productDescription) {
        this.refs.productDescription.textContent = config.description || 'Get this product on subscription and save!';
      }

      // Set the product form data
      if (this.refs.productForm) {
        this.refs.productForm.dataset.productId = config.productId;
        this.refs.productForm.dataset.variantId = config.variantId;
        this.refs.productForm.dataset.sellingPlan = config.sellingPlanId;
      }

      // Fetch and update product details if needed
      if (config.productId && !config.title) {
        await this.fetchProductDetails(config.productId);
      }
    } catch (error) {
      // Failed to load upsell product
    }
  }

  /**
   * Fetches product details from Shopify
   *
   * @param {string} productId - The product ID
   */
  async fetchProductDetails(productId) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      if (!response.ok) throw new Error('Failed to fetch product');

      const product = await response.json();

      // Update modal with fetched product details
      if (this.refs.productTitle && product.title) {
        this.refs.productTitle.textContent = product.title;
      }

      if (this.refs.productImage && product.featured_image) {
        this.refs.productImage.src = product.featured_image;
        this.refs.productImage.alt = product.title;
      }

      if (this.refs.productPrice && product.price) {
        this.refs.productPrice.textContent = this.formatMoney(product.price);
      }
    } catch (error) {
      // Failed to fetch product details
    }
  }

  /**
   * Handles accepting the upsell
   */
  async handleAcceptUpsell() {
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.refs.acceptButton?.classList.add('loading');

    try {
      const config = this.currentUpsellConfig;
      if (!config) throw new Error('No upsell configuration found');

      // Get the current variant ID from the form (which may have been updated by radio button selection)
      const currentVariantId = this.refs.variantIdInput?.value || config.variantId;
      const currentSellingPlan = this.refs.sellingPlanInput?.value || config.sellingPlanId;

        // Prepare cart item with selling plan
      const cartItem = {
        id: currentVariantId,
        quantity: 1
      };

      // Add selling plan if configured
      if (currentSellingPlan) {
        cartItem.selling_plan = currentSellingPlan;
      }

      // Get all cart-related section IDs
      const cartSectionIds = [];

      // Get cart items sections
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartSectionIds.push(item.dataset.sectionId);
        }
      });

      // Get cart icon section if it exists
      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon instanceof HTMLElement && cartIcon.dataset.sectionId) {
        cartSectionIds.push(cartIcon.dataset.sectionId);
      }

      // Add to cart with sections
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [cartItem],
          sections: cartSectionIds.join(',')
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add subscription to cart');
      }

      const result = await response.json();

      // Import and dispatch events to open drawer and update cart
      const { CartAddEvent, CartUpdateEvent } = await import('@theme/events');

      // Dispatch the CartAddEvent (this will open the cart drawer)
      document.dispatchEvent(
        new CartAddEvent(result, currentVariantId, {
          source: 'subscription-upsell-modal',
          itemCount: 1,
          productId: config.productId,
          sections: result.sections,
          didError: false
        })
      );

      // Also dispatch CartUpdateEvent to ensure cart items component updates
      if (result.sections) {
        document.dispatchEvent(
          new CartUpdateEvent({}, 'subscription-upsell-modal', {
            itemCount: result.item_count || 1,
            source: 'subscription-upsell-modal',
            sections: result.sections
          })
        );
      }

      // Close modal
      await this.closeDialog();

      // Show success message
      this.showSuccessMessage();
    } catch (error) {
      this.showErrorMessage(error.message);
    } finally {
      this.isProcessing = false;
      this.refs.acceptButton?.classList.remove('loading');
    }
  }

  /**
   * Handles declining the upsell
   */
  async handleDeclineUpsell() {
    // Track declined upsell (for analytics)
    this.trackUpsellEvent('declined');

    // Close the modal
    await this.closeDialog();
  }

  /**
   * Shows a success message
   */
  showSuccessMessage() {
    // This could trigger a toast notification or update the cart drawer
    document.dispatchEvent(new CustomEvent('notification:show', {
      detail: {
        type: 'success',
        message: 'Subscription added to cart!'
      }
    }));
  }

  /**
   * Shows an error message
   *
   * @param {string} message - The error message
   */
  showErrorMessage(message) {
    document.dispatchEvent(new CustomEvent('notification:show', {
      detail: {
        type: 'error',
        message: message || 'Failed to add subscription to cart'
      }
    }));
  }

  /**
   * Tracks upsell events for analytics
   *
   * @param {string} action - The action taken (shown, accepted, declined)
   */
  trackUpsellEvent(action) {
    // Send to analytics (Google Analytics, Shopify Analytics, etc.)
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish('subscription_upsell', {
        action: action,
        productId: this.currentUpsellConfig?.productId,
        sellingPlanId: this.currentUpsellConfig?.sellingPlanId
      });
    }
  }

  /**
   * Gets the list of products that have been shown the upsell
   *
   * @returns {Array} Array of product IDs
   */
  getShownProducts() {
    const data = sessionStorage.getItem(this.sessionStorageKey);
    return data ? JSON.parse(data) : [];
  }

  /**
   * Marks a product as having been shown the upsell
   *
   * @param {string} productId - The product ID
   */
  markAsShown(productId) {
    const shown = this.getShownProducts();
    if (!shown.includes(productId)) {
      shown.push(productId);
      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(shown));
    }

    // Track that upsell was shown
    this.trackUpsellEvent('shown');
  }

  /**
   * Formats money value
   *
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  }
}

// Register the custom element
if (!customElements.get('subscription-upsell-modal')) {
  customElements.define('subscription-upsell-modal', SubscriptionUpsellModal);
}